<template>
  <div class="w-full">
    <label v-if="label" :for="id" class="block mb-2 text-sm font-medium text-gray-700">{{ label }}</label>
    <div
      class="relative flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
      :class="{ 'border-blue-500': isFocused, 'border-red-500': error }"
      @click="triggerInput"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      tabindex="0"
      @focus="isFocused = true"
      @blur="isFocused = false"
    >
      <input
        ref="fileInput"
        :id="id"
        type="file"
        :accept="accept"
        class="hidden"
        @change="onFileChange"
      />
      <div v-if="!fileName && !previewUrl" class="flex flex-col items-center">
        <svg class="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4a1 1 0 011-1h8a1 1 0 011 1v12m-4 4h-4a1 1 0 01-1-1v-1h6v1a1 1 0 01-1 1z" />
        </svg>
        <span class="text-gray-500 text-sm">{{ placeholder || 'Click or drag an image here to upload' }}</span>
      </div>
      <div v-else class="flex flex-col items-center w-full">
        <img v-if="previewUrl" :src="previewUrl" alt="Preview" class="w-24 h-24 object-cover rounded mb-2 border" />
        <span class="truncate text-gray-700 text-sm">{{ fileName }}</span>
        <button v-if="fileName" type="button" class="mt-2 text-xs text-red-500 hover:underline" @click.stop="removeFile">Remove</button>
      </div>
    </div>
    <p v-if="errorMessage" class="mt-2 text-xs text-red-600">{{ errorMessage }}</p>
  </div>
</template>

<script>
export default {
  name: 'V2BaseFileInput',
  props: {
    value: File,
    label: String,
    accept: {
      type: String,
      default: 'image/*',
    },
    placeholder: String,
    maxSizeMB: {
      type: Number,
      default: 2,
    },
    error: Boolean,
    errorMessage: String,
    id: {
      type: String,
      default: () => `file-input-${Math.random().toString(36).substr(2, 9)}`,
    },
  },
  data() {
    return {
      fileName: '',
      previewUrl: '',
      isFocused: false,
      localError: '',
    };
  },
  watch: {
    value(newVal) {
      if (!newVal) {
        this.fileName = '';
        this.previewUrl = '';
      }
    },
  },
  methods: {
    triggerInput() {
      this.$refs.fileInput.click();
    },
    onFileChange(e) {
      const file = e.target.files[0];
      this.handleFile(file);
    },
    onDrop(e) {
      const file = e.dataTransfer.files[0];
      this.handleFile(file);
      this.isFocused = false;
    },
    onDragOver() {
      this.isFocused = true;
    },
    onDragLeave() {
      this.isFocused = false;
    },
    handleFile(file) {
      this.localError = '';
      if (!file) return;
      if (file.size > this.maxSizeMB * 1024 * 1024) {
        this.localError = `File size must be less than ${this.maxSizeMB}MB`;
        this.$emit('input', null);
        this.$emit('error', this.localError);
        return;
      }
      if (this.accept && !file.type.match(this.accept.replace('*', '.*'))) {
        this.localError = 'Invalid file type';
        this.$emit('input', null);
        this.$emit('error', this.localError);
        return;
      }
      this.fileName = file.name;
      this.$emit('input', file);
      this.$emit('error', '');
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = e => {
          this.previewUrl = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        this.previewUrl = '';
      }
    },
    removeFile() {
      this.fileName = '';
      this.previewUrl = '';
      this.$emit('input', null);
      this.$emit('error', '');
      this.$refs.fileInput.value = '';
    },
  },
  computed: {
    errorText() {
      return this.errorMessage || this.localError;
    },
  },
};
</script>
